import {
  AppstoreOutlined,
  BellOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  KeyOutlined,
  LineChartOutlined,
  PlayCircleOutlined,
  SafetyOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { PageHeading } from '../components/PageHeading';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';
import { websiteUrl } from '../lib/websiteUrl';

type GuideSection = {
  id: string;
  icon: ReactNode;
  title: string;
  route: string;
  copy: { lede: string; does: string; start: string };
  tag?: string;
};

/**
 * Member-facing user manual — one section per real menu item (same order as
 * the left sidebar in Layout.tsx), each explaining what the page is, what it
 * does, and how to start using it. Route paths are plain data, not
 * translated (see the note on `Dictionary['guide']` in types.ts).
 */
export function Guide() {
  const { dict } = useLanguage();
  const { isB2BOrg } = useSession();

  const sections: GuideSection[] = useMemo(
    () => [
      {
        id: 'dashboard',
        icon: <AppstoreOutlined aria-hidden="true" />,
        title: dict.common.dashboard,
        route: '/dashboard',
        copy: dict.guide.sections.dashboard,
      },
      {
        id: 'keys',
        icon: <KeyOutlined aria-hidden="true" />,
        title: dict.common.apiKeys,
        route: '/keys',
        copy: dict.guide.sections.keys,
      },
      {
        id: 'usage',
        icon: <LineChartOutlined aria-hidden="true" />,
        title: dict.common.usage,
        route: '/usage',
        copy: dict.guide.sections.usage,
      },
      {
        id: 'reports',
        icon: <FileTextOutlined aria-hidden="true" />,
        title: dict.common.reports,
        route: '/reports',
        copy: dict.guide.sections.reports,
      },
      {
        id: 'models',
        icon: <DeploymentUnitOutlined aria-hidden="true" />,
        title: dict.common.models,
        route: '/models',
        copy: dict.guide.sections.models,
      },
      {
        id: 'topup',
        icon: <CreditCardOutlined aria-hidden="true" />,
        title: dict.common.topUp,
        route: '/topup',
        copy: { ...dict.guide.sections.topUp, lede: dict.guide.sections.topUp.lede(isB2BOrg) },
        tag: dict.guide.tagAdminOnly(isB2BOrg),
      },
      {
        id: 'team',
        icon: <TeamOutlined aria-hidden="true" />,
        title: dict.common.team,
        route: '/team',
        copy: { ...dict.guide.sections.team, lede: dict.guide.sections.team.lede(isB2BOrg) },
      },
      {
        id: 'playground',
        icon: <ExperimentOutlined aria-hidden="true" />,
        title: dict.common.playground,
        route: '/playground',
        copy: dict.guide.sections.playground,
        tag: dict.guide.tagEnvGated,
      },
      {
        id: 'profile',
        icon: <SafetyOutlined aria-hidden="true" />,
        title: dict.common.editProfile,
        route: '/profile/edit',
        copy: dict.guide.sections.profile,
        tag: dict.guide.tagViaAvatarMenu,
      },
      {
        id: 'notifications',
        icon: <BellOutlined aria-hidden="true" />,
        title: dict.notifications.historyHeading,
        route: '/notifications',
        copy: { ...dict.guide.sections.notifications, does: dict.guide.sections.notifications.does(isB2BOrg) },
        tag: dict.guide.tagViaBell,
      },
    ],
    [dict, isB2BOrg],
  );

  const [activeId, setActiveId] = useState<string>('dashboard');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const anchorRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const [indicator, setIndicator] = useState({ top: 0, height: 0 });

  // Scrollspy: whichever observed section is nearest the top of the
  // "reading band" (15%–30% down the viewport) becomes active. A single
  // shared observer, not one per section — sections never change identity
  // after mount, so it is set up once.
  useLayoutEffect(() => {
    // The last section is usually shorter than the observer's reading band
    // (15%–30% down the viewport), so scrolling to the actual bottom of the
    // page can leave it already scrolled past that band on both edges —
    // neither it nor the one above report isIntersecting, and the observer
    // callback simply stops firing with the second-to-last item stuck
    // active. Reaching the bottom of the document is unambiguous, so both
    // this check and the callback below force the last item active there;
    // having both agree, instead of only one, is what keeps whichever fires
    // last from re-asserting a stale entry over the other's fix.
    function isAtBottom() {
      return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    }
    function forceLastIfAtBottom() {
      if (!isAtBottom()) return false;
      const lastId = sections[sections.length - 1]?.id;
      if (!lastId) return false;
      setActiveId(lastId);
      return true;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (forceLastIfAtBottom()) return;
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = visible[0]?.target.getAttribute('data-guide-section');
        if (id) setActiveId(id);
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));

    forceLastIfAtBottom();
    window.addEventListener('scroll', forceLastIfAtBottom, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', forceLastIfAtBottom);
    };
  }, [sections]);

  // The moving indicator bar tracks whichever anchor link is active — offset
  // read straight off the DOM (offsetTop/offsetHeight against the list,
  // which is `position: relative`) rather than duplicated in state.
  useLayoutEffect(() => {
    const el = anchorRefs.current[activeId];
    if (el) setIndicator({ top: el.offsetTop, height: el.offsetHeight });
  }, [activeId]);

  function scrollToSection(id: string) {
    const el = sectionRefs.current[id];
    if (!el) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    setActiveId(id);
  }

  return (
    <div className="page guide-page">
      <div className="guide-head">
        <PageHeading subtitle={dict.guide.intro}>{dict.guide.title}</PageHeading>
      </div>

      <div className="guide-layout">
        <main className="guide-main">
          {sections.map((s) => (
            <motion.section
              key={s.id}
              ref={(el) => {
                sectionRefs.current[s.id] = el;
              }}
              data-guide-section={s.id}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="guide-section"
            >
              <div className="guide-section-head">
                <span className="guide-section-icon">{s.icon}</span>
                <div className="guide-section-titles">
                  <h2>{s.title}</h2>
                  <span className="guide-section-route">{s.route}</span>
                </div>
                {s.tag && <span className="guide-section-tag">{s.tag}</span>}
              </div>
              <p className="guide-section-lede">{s.copy.lede}</p>
              <div className="guide-facts">
                <div className="guide-field">
                  <div className="guide-field-label">
                    <span className="guide-field-icon guide-field-icon--does" aria-hidden="true">
                      <CheckCircleOutlined />
                    </span>
                    <b>{dict.guide.fieldDoes}</b>
                  </div>
                  <p className="guide-field-body">{s.copy.does}</p>
                </div>
                <div className="guide-field">
                  <div className="guide-field-label">
                    <span className="guide-field-icon guide-field-icon--start" aria-hidden="true">
                      <PlayCircleOutlined />
                    </span>
                    <b>{dict.guide.fieldStart}</b>
                  </div>
                  <p className="guide-field-body">{s.copy.start}</p>
                </div>
              </div>
              {s.id === 'keys' && websiteUrl() && (
                <a
                  href={`${websiteUrl()}docs#connect`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="guide-link-btn"
                >
                  {dict.guide.keysConnectDocsLabel}
                </a>
              )}
            </motion.section>
          ))}
        </main>

        <aside className="guide-anchor">
          <div className="guide-anchor-sticky">
            <div className="guide-anchor-label">{dict.guide.onThisPage}</div>
            <nav className="guide-anchor-list">
              <span
                className="guide-anchor-indicator"
                style={{ transform: `translateY(${indicator.top}px)`, height: indicator.height }}
              />
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  ref={(el) => {
                    anchorRefs.current[s.id] = el;
                  }}
                  className={`guide-anchor-item${activeId === s.id ? ' active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(s.id);
                  }}
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  );
}
